import type { FC, InputHTMLAttributes, ReactNode } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn";

const fieldVariants = cva(
  "flex w-full items-center gap-8 rounded-medium border bg-neutral-white p-16 transition-colors focus-within:border-brand-blue",
  {
    variants: {
      state: {
        default: "border-neutral-mid-grey",
        error: "border-brand-red",
        info: "border-neutral-mid-grey",
      },
    },
    defaultVariants: {
      state: "default",
    },
  },
);

type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, "size"> &
  VariantProps<typeof fieldVariants> & {
    label?: ReactNode;
    message?: ReactNode;
    iconLeft?: ReactNode;
    iconRight?: ReactNode;
  };

export const Input: FC<InputProps> = ({
  className,
  label,
  message,
  iconLeft,
  iconRight,
  state = "default",
  disabled,
  id,
  required,
  ...props
}) => (
  <div className="flex w-full flex-col gap-8">
    {label && (
      <label htmlFor={id} className={cn("text-16", state === "error" ? "text-brand-red" : "text-neutral-black")}>
        {label}
        {required && <span className="text-brand-red"> *</span>}
      </label>
    )}
    <div className={cn(fieldVariants({ state }), disabled && "bg-neutral-black/5", className)}>
      {iconLeft && <span className="size-24 shrink-0 text-neutral-black">{iconLeft}</span>}
      <input
        id={id}
        disabled={disabled}
        required={required}
        className="min-w-0 flex-1 bg-transparent text-16 text-neutral-black outline-none placeholder:text-neutral-black/35 disabled:cursor-not-allowed"
        {...props}
      />
      {iconRight && <span className="size-24 shrink-0 text-neutral-black">{iconRight}</span>}
    </div>
    {message && <p className={cn("text-14", state === "error" ? "text-brand-red" : "text-neutral-grey")}>{message}</p>}
  </div>
);
